import React from 'react';
import { Link } from 'react-router-dom';
import {
  Umbrella,
  Sun,
  ShieldCheck,
  MapPin,
  Phone,
  MessageSquare,
  Sparkles,
  ChevronRight,
  CheckCircle2,
  HelpCircle,
  Compass
} from 'lucide-react';
import { BeachHireSection } from '../components/BeachHireSection';
import { RESTAURANT_INFO } from '../data/restaurantData';

export const BeachHirePage: React.FC = () => {
  const handleWhatsAppInquiry = () => {
    const message = encodeURIComponent(
      "Hello Frankie's! 🏖️ I'd like to ask about hiring deck chairs, sun loungers, and windbreaks on Ramsgate Beach."
    );
    window.open(`https://wa.me/${RESTAURANT_INFO.contact.whatsappNumber}?text=${message}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex-1 bg-[#EEEFE9] min-h-screen">
      {/* 1. Page Breadcrumbs & Hero Header */}
      <div className="bg-gradient-to-b from-[#0070E0] via-[#005bb5] to-[#004b96] text-white py-12 sm:py-16 relative overflow-hidden">
        {/* Subtle decorative glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-300/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-sky-300/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center gap-2 text-xs text-sky-200 uppercase font-heading font-bold mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-sky-300" />
            <span className="text-[#ECD87A]">Beach Hire</span>
          </nav>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-400 text-black text-xs font-heading font-black uppercase tracking-wider mb-4 shadow-md">
              <Umbrella className="w-3.5 h-3.5" />
              <span>RAMSGATE SANDS • DECKCHAIRS &amp; BEACH HIRE</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black font-heading tracking-tight uppercase leading-tight">
              SEASIDE BEACH EQUIPMENT HIRE
            </h1>

            <p className="mt-3 text-sm sm:text-base text-sky-100 leading-relaxed max-w-2xl font-medium">
              Enjoy a relaxed, comfortable day on Ramsgate Main Sands. Hire authentic striped deck chairs, padded sun loungers, seaside parasols, and heavy-duty windbreaks directly beside Frankie’s kiosk.
            </p>

            {/* Quick Highlights */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleWhatsAppInquiry}
                className="bg-[#25D366] hover:bg-[#20ba5a] text-white font-heading font-black text-xs uppercase tracking-wider px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 transition-transform active:scale-95 cursor-pointer"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Reserve Via WhatsApp</span>
              </button>

              <a
                href={RESTAURANT_INFO.contact.phoneHref}
                className="bg-white/15 hover:bg-white/25 text-white font-heading font-bold text-xs uppercase tracking-wider px-5 py-3 rounded-xl border border-white/20 flex items-center gap-2 transition-all cursor-pointer"
              >
                <Phone className="w-4 h-4 text-amber-300" />
                <span>Call {RESTAURANT_INFO.contact.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Key Information Bar */}
      <div className="bg-white border-b border-[#dde0d5] py-4 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-bold text-[#000000]">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <Sun className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <div className="font-heading uppercase text-amber-800">Available Daily</div>
                <div className="text-[#526b74] font-normal text-[11px]">Direct on the sand • Weather Permitting</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 text-[#0070E0] flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4" />
              </div>
              <div>
                <div className="font-heading uppercase text-[#0070E0]">On the Sands</div>
                <div className="text-[#526b74] font-normal text-[11px]">Kiosk 1, Marina Esplanade, Ramsgate Beach</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-4 h-4" />
              </div>
              <div>
                <div className="font-heading uppercase text-emerald-800">No Booking Needed</div>
                <div className="text-[#526b74] font-normal text-[11px]">Walk-in first-come, or WhatsApp for large groups</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Main Beach Equipment Hire Showcase & Pricing */}
      <div className="py-8">
        <BeachHireSection />
      </div>

      {/* 4. Tips for your day on Ramsgate Sands */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="bg-white rounded-3xl p-6 sm:p-10 border border-[#dde0d5] shadow-xs">
          <div className="flex items-center gap-2 text-xs font-extrabold text-[#0070E0] uppercase tracking-wider font-heading mb-2">
            <HelpCircle className="w-4 h-4 text-amber-500" />
            <span>BEACH HIRE FAQS &amp; INFORMATION</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-extrabold uppercase font-heading text-[#000000] mb-6">
            EVERYTHING YOU NEED TO KNOW BEFORE YOU SIT BACK
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs sm:text-sm text-[#526b74] leading-relaxed">
            <div className="p-4 rounded-2xl bg-[#f8f9f5] border border-[#e4e8dd] space-y-1.5">
              <h3 className="font-heading font-black text-[#000000] uppercase text-sm">
                How does daily beach hire work?
              </h3>
              <p>
                Simply walk up to Frankie's Kiosk 1 on Marina Esplanade. Pick your equipment — deck chairs, sun loungers, parasols, or windbreaks — and our team will set you up directly on the beach sand. Keep your hire slip and return when you are finished for the day.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-[#f8f9f5] border border-[#e4e8dd] space-y-1.5">
              <h3 className="font-heading font-black text-[#000000] uppercase text-sm">
                Can we eat Frankie's food in our deck chairs?
              </h3>
              <p>
                Yes, absolutely! All our burgers, loaded chips, hot dogs, cold pints, and seaside slushies can be taken directly to your deck chair or lounger setup on the sand.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-[#f8f9f5] border border-[#e4e8dd] space-y-1.5">
              <h3 className="font-heading font-black text-[#000000] uppercase text-sm">
                Can I reserve gear for a large family or party?
              </h3>
              <p>
                Yes! If you are bringing a group or celebrating a birthday, send us a quick WhatsApp message with your party size and we'll have a reserved section of deck chairs and windbreaks ready for you.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-[#f8f9f5] border border-[#e4e8dd] space-y-1.5">
              <h3 className="font-heading font-black text-[#000000] uppercase text-sm">
                Where is Frankie's Beach Hire located?
              </h3>
              <p>
                Right on Ramsgate Main Sands, at Kiosk 1, Marina Esplanade (CT11 8LS). Look for the blue and yellow Frankie's kiosk and colourful seaside deck chairs right by the tide line!
              </p>
            </div>
          </div>

          {/* Bottom call to action */}
          <div className="mt-8 pt-6 border-t border-[#e4e8dd] flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xs font-bold text-[#000000]">
              <Compass className="w-4 h-4 text-[#0070E0]" />
              <span>Planning your visit today? Drop by Kiosk 1 or reach out on WhatsApp.</span>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/menu"
                className="bg-[#0070E0] hover:bg-[#005bb5] text-white font-heading font-black text-xs uppercase tracking-wider px-5 py-2.5 rounded-xl transition-all shadow-sm"
              >
                View Food &amp; Drink Menu
              </Link>
              <Link
                to="/contact"
                className="bg-[#f0f2eb] hover:bg-[#e4e8dd] text-[#000000] font-heading font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-xl transition-all border border-[#dde0d5]"
              >
                Find Us / Contact
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
